import {
  type FeedbackContext,
  type FeedbackKind,
  type FeedbackOutcome,
  TEXT_MAX,
  sendFeedback,
} from '../state/feedback';
import { must } from './dom';
import type { Overlay } from './overlays';
import { str } from './strings';

/**
 * The drop box panel (GDD §13): the game's one text input.
 *
 * Two rules shape all of it. **A failure never costs the message** — every
 * outcome keeps the typed text exactly where it is and leaves send live, because
 * losing a paragraph somebody wrote is the one failure this feature cannot have.
 * And **what rides along is printed here**, above the send, so the privacy claim
 * is checkable rather than asserted.
 */

/**
 * How close to the cap the countdown appears. A counter over an empty box reads
 * as a demand for length; one in the last stretch is a warning.
 */
const COUNT_FROM = 120;

/** The chips, in the order they are offered. The ids go on the wire. */
const KINDS: FeedbackKind[] = ['bug', 'idea', 'grid', 'other'];

export class FeedbackPanel {
  private kind: FeedbackKind | null = null;
  private sending = false;

  constructor(
    private readonly overlay: Overlay,
    /** The grid the sender is on, read at send time rather than at open. */
    private readonly context: () => FeedbackContext,
  ) {
    must('fbKinds').addEventListener('click', (e) => {
      const chip = (e.target as Element | null)?.closest<HTMLElement>('.fb-kind');
      if (!chip) return;
      const picked = chip.dataset.kind as FeedbackKind | undefined;
      if (!picked) return;
      // A second press clears it. The chips are triage help, not a taxonomy to
      // satisfy, so there has to be a way back to having said nothing.
      this.kind = this.kind === picked ? null : picked;
      this.paintKinds();
    });

    this.text.addEventListener('input', () => this.paintCount());
    must('fbSend').addEventListener('click', () => void this.send());
    must('fbCancel').addEventListener('click', () => this.overlay.close());
    must('fbDoneClose').addEventListener('click', () => this.overlay.close());
  }

  private get text(): HTMLTextAreaElement {
    return must<HTMLTextAreaElement>('fbText');
  }

  private get contact(): HTMLInputElement {
    return must<HTMLInputElement>('fbContact');
  }

  /**
   * Show the form, ready to type.
   *
   * A draft survives a close and reopen: dismissing a panel by accident must not
   * cost what was typed either. Only a message that actually landed clears it.
   */
  open(): void {
    // `hidden`, not a display style: it takes the panel out of the reading
    // order too, and a screen reader must not find a form that is not there.
    must('fbForm').hidden = false;
    must('fbDone').hidden = true;
    must('fbMsg').hidden = true;
    this.paint();
    this.overlay.open();
  }

  /** Re-apply copy. Called on open and again on every language change. */
  paint(): void {
    const s = str();
    this.text.placeholder = s.feedbackPlaceholder;
    this.contact.placeholder = s.feedbackContactHint;
    must('fbSend').textContent = this.sending ? s.feedbackSending : s.feedbackSend;
    must('fbDoneText').textContent = s.feedbackDone;
    this.paintKinds();
    this.paintCount();
    this.paintNote();
  }

  private paintKinds(): void {
    const words = str().feedbackKinds;
    must('fbKinds').innerHTML = KINDS.map(
      (k) =>
        `<button class="fb-kind" type="button" data-kind="${k}"` +
        ` aria-pressed="${this.kind === k}">${words[k]}</button>`,
    ).join('');
  }

  private paintCount(): void {
    const left = TEXT_MAX - this.text.value.length;
    const count = must('fbCount');
    count.hidden = left > COUNT_FROM;
    count.textContent = str().feedbackLeft(left);
    must<HTMLButtonElement>('fbSend').disabled = !this.text.value.trim() || this.sending;
  }

  /** The whole of what travels with the message, stated before it goes. */
  private paintNote(): void {
    const s = str();
    const ctx = this.context();
    const mode = s.feedbackModes[ctx.mode];
    must('fbNote').textContent =
      ctx.grid === undefined
        ? s.feedbackAttachedNoGrid(mode, ctx.build)
        : s.feedbackAttached(ctx.grid, mode, ctx.build);
  }

  private async send(): Promise<void> {
    const text = this.text.value.trim();
    if (!text || this.sending) return;
    this.sending = true;
    must('fbMsg').hidden = true;
    this.paint();

    // An unpicked chip sends `other`: no message should be lost to a taxonomy
    // its sender did not care about.
    const outcome = await sendFeedback({
      kind: this.kind ?? 'other',
      text,
      contact: this.contact.value.trim(),
      ctx: this.context(),
    });

    this.sending = false;
    this.paint();
    if (outcome === 'ok') {
      this.text.value = '';
      this.contact.value = '';
      this.kind = null;
      must('fbForm').hidden = true;
      must('fbDone').hidden = false;
      return;
    }
    const msg = must('fbMsg');
    msg.textContent = problem(outcome);
    msg.hidden = false;
  }
}

/** The copy for an outcome that is not `ok`. Never silent (GDD §13). */
function problem(outcome: Exclude<FeedbackOutcome, 'ok'>): string {
  const s = str();
  if (outcome === 'throttled') return s.feedbackFailThrottled;
  if (outcome === 'invalid') return s.feedbackFailInvalid;
  return s.feedbackFailOffline;
}
